import { envRank, type SelectableTenant } from './tenantGrouping'

export interface CapScopeOption {
  value: string
  label: string
}

/**
 * Ambientes ofertáveis numa RESTRIÇÃO (cap) por ambiente.
 *
 * Deliberadamente NÃO deriva do escopo de tenants do perfil: uma cap de
 * ambiente casa por `tenants.environment` (`rbac.py:1209`) e vale para todo
 * tenant daquele ambiente, em qualquer organização — o escopo do perfil não
 * entra na conta. Derivando dele, perfil sem OU (o caso de todos os perfis de
 * produção) não oferecia ambiente nenhum.
 *
 * `value` é a chave de casamento e sai CRU do banco (minúsculo); a maiúscula
 * fica só no `label`. Trocar isso faz `resolve_template_cap` devolver `None` e
 * a restrição para de valer em silêncio.
 *
 * O vocabulário é o dos tenants existentes, não uma lista fixa: o CHECK de
 * `tenants.environment` aceita qualquer `^[a-z][a-z0-9_-]{1,7}$` desde a
 * MIGRATION_081.
 */
export function environmentOptions(tenants: SelectableTenant[]): CapScopeOption[] {
  const envs = [...new Set(tenants.map((t) => t.environment).filter(Boolean))]
  return envs
    .sort((a, b) => envRank(a) - envRank(b) || a.localeCompare(b))
    .map((env) => ({ value: env, label: env.toUpperCase() }))
}

/**
 * Tenants ofertáveis numa RESTRIÇÃO por tenant.
 *
 * Prefere o alcance do próprio perfil (`tenants` do payload — união da lista
 * legada com os tenants das orgs onde o perfil está disponível/aplicado). Cap
 * nunca concede, então oferecer além disso seria seguro; mas linha de restrição
 * apontando para tenant que aquele perfil não alcança nunca casa, e restrição
 * que não casa tem cara de proteção sem ser. A queda para `all` cobre o perfil
 * novo, que ainda não alcança nada.
 *
 * `value` é `String(id)` porque é assim que a cap por tenant é gravada e
 * comparada (`rbac.py:1197`).
 */
export function capTenantOptions(
  reach: SelectableTenant[],
  all: SelectableTenant[],
): CapScopeOption[] {
  const source = reach.length > 0 ? reach : all
  return [...source]
    .sort(
      (a, b) =>
        envRank(a.environment) - envRank(b.environment) ||
        a.environment.localeCompare(b.environment) ||
        a.name.localeCompare(b.name, 'pt-BR'),
    )
    .map((t) => ({
      value: String(t.id),
      label: `[${t.environment.toUpperCase()}] ${t.name}`,
    }))
}
